import type { QueryClient } from '@tanstack/react-query';
import type { RealtimeEvent, UserId } from '@cyphex/types';
import { CHAT_KEYS } from '../constants';
import { typingStore } from '../store/typingStore';
import { draftStore } from '../store/draftStore';
import { applyConversationPatch, bumpConversation, patchSelfState } from './conversationCache';
import { applyReceipt } from './readStateCache';
import { appendMessage, dropMessage, patchMessage, tombstoneMessage } from './timelineCache';
import { keepViewerState, reactionsForViewer } from './viewerState';

/**
 * The single reducer from a gateway event to cached state. Keeping it pure and
 * outside React means a message storm mutates the cache without re-rendering
 * anything that is not actually subscribed to the changed slice.
 */
export function applyRealtimeEvent(client: QueryClient, event: RealtimeEvent, meId: UserId | null): void {
  switch (event.type) {
    case 'message.new': {
      const isOwn = event.message.sender?.id === meId;
      appendMessage(client, event.conversationId, event.message, event.clientMsgId);
      bumpConversation(client, event.conversationId, event.message, isOwn);
      if (event.message.sender) {
        typingStore.clear(event.conversationId, event.message.sender.id);
      }
      // MY OWN message that still carries `scheduledFor` is the dispatcher
      // delivering a send-later. That is the only signal the "N scheduled"
      // strip gets that its queue just shrank, and without it the strip would
      // keep counting a message the reader can already see above it.
      if (isOwn && event.message.scheduledFor) {
        void client.invalidateQueries({ queryKey: CHAT_KEYS.scheduled(event.conversationId) });
      }
      break;
    }
    case 'message.updated':
      // The message is everybody's; the star, the reminder and "my" reactions
      // on it are this reader's. See `keepViewerState`.
      patchMessage(client, event.conversationId, event.messageId, (mine) =>
        keepViewerState(mine, event.message),
      );
      break;
    case 'message.deleted':
      if (event.mode === 'hard') dropMessage(client, event.conversationId, event.messageId);
      else tombstoneMessage(client, event.conversationId, event.messageId, new Date().toISOString());
      break;
    case 'reaction.added':
    case 'reaction.removed':
      patchMessage(client, event.conversationId, event.messageId, (message) => ({
        ...message,
        reactions: reactionsForViewer(message.reactions, event, meId),
      }));
      break;
    case 'typing.start':
      if (event.user.id !== meId) {
        typingStore.mark(
          event.conversationId,
          event.user.id,
          event.user.displayName,
          new Date(event.expiresAt).getTime(),
        );
      }
      break;
    case 'typing.stop':
      typingStore.clear(event.conversationId, event.user.id);
      break;
    case 'read.receipt':
      applyReceipt(client, event.conversationId, event.userId, event.seq);
      break;
    /* ---- Papercuts, all on the caller's private `user:` topic ------------- */
    case 'conversation.draft.updated': {
      patchSelfState(client, event.conversationId, {
        draft: event.draft,
        draftUpdatedAt: event.updatedAt,
      });
      // Mirror into the local store so the composer on THIS device picks up
      // what another device typed — but never clobber text being typed here.
      const local = draftStore.select(event.conversationId);
      if (local.text.length === 0 && event.draft?.text) {
        draftStore.set(event.conversationId, { text: event.draft.text });
      }
      break;
    }
    case 'message.saved':
    case 'message.unsaved':
      patchMessage(client, event.conversationId, event.messageId, (message) => ({
        ...message,
        isSaved: event.type === 'message.saved',
      }));
      break;
    case 'message.reminder.set':
    case 'message.reminder.cleared':
      patchMessage(client, event.conversationId, event.messageId, (message) => ({
        ...message,
        reminderAt: event.remindAt,
      }));
      break;
    case 'unread.changed':
      for (const entry of event.conversations) {
        patchSelfState(client, entry.conversationId, {
          unreadCount: entry.unreadCount,
          unreadMentionCount: entry.unreadMentionCount,
        });
      }
      // The counter is only half of what changed. This event is the ONLY signal
      // a client gets for a conversation whose topic it is not subscribed to —
      // i.e. any thread that is not currently open — and it carries no message,
      // so the row's preview text and timestamp would stay on the previous
      // message until a reload. Refetch the list so the row reads correctly.
      void client.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      break;
    /*
     * WHO IS IN THE ROOM CHANGED.
     *
     * Both frames are DURABLE and both arrive twice on purpose: once on
     * `conversation:` for people with the thread open, and once on the affected
     * person's private `user:` topic — which is the only one that can reach
     * somebody who has just been added, because a client subscribes to a
     * conversation by OPENING it and they do not know it exists yet.
     *
     * Nothing handled either of them, so the roster, the member count and the
     * chat list all stayed on the pre-change answer until a reload. The list
     * refetch is what puts a new group on the joiner's screen and takes a left
     * one off the leaver's.
     */
    case 'conversation.member.added':
    case 'conversation.member.removed':
      void client.invalidateQueries({ queryKey: CHAT_KEYS.members(event.conversationId) });
      void client.invalidateQueries({ queryKey: CHAT_KEYS.conversation(event.conversationId) });
      void client.invalidateQueries({ queryKey: ['chat', 'conversations'] });
      break;
    case 'conversation.updated': {
      /* `patch.avatarColor` is widened to `string` on the wire; the entity
         narrows it to `AvatarColor`, so it is applied by the list renderer
         rather than blind-merged here. */
      const { avatarColor: _tint, ...patch } = event.patch;
      applyConversationPatch(client, event.conversationId, patch);
      break;
    }
    default:
      break;
  }
}
